import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  TrendingUp, 
  Users, 
  ShoppingCart, 
  Clock,
  Star,
  Eye,
  MousePointer
} from 'lucide-react';

const CaseStudies = () => {
  const [activeComparison, setActiveComparison] = useState(0);

  const caseStudies = [
    {
      title: "Local Restaurant Chain",
      category: "Food & Beverage",
      challenge: "Outdated website with no mobile optimization and poor online ordering system",
      solution: "Modern responsive design with integrated online ordering and reservation system",
      results: {
        traffic: "+245%",
        conversions: "+180%", 
        orders: "+320%",
        time: "3 weeks"
      },
      beforeImage: "/images/website_comparison_0.jpg",
      afterImage: "/images/website_comparison_1.jpg",
      testimonial: "SuccessPro transformed our online presence. Online orders increased by 320% in the first month!",
      client: "Maria Rodriguez, Owner"
    },
    {
      title: "Professional Services Firm",
      category: "Legal Services",
      challenge: "Complex navigation, outdated design, and poor lead generation",
      solution: "Streamlined user experience with clear CTAs and lead capture forms",
      results: {
        traffic: "+190%",
        conversions: "+275%",
        leads: "+400%",
        time: "4 weeks"
      },
      beforeImage: "/images/website_comparison_2.jpg",
      afterImage: "/images/website_comparison_3.jpg",
      testimonial: "The new website generates 4x more qualified leads. Best investment we've made.",
      client: "David Chen, Managing Partner"
    },
    {
      title: "E-commerce Startup",
      category: "Retail",
      challenge: "Poor user experience leading to high cart abandonment rates",
      solution: "Optimized checkout process and mobile-first design approach",
      results: {
        traffic: "+165%",
        conversions: "+220%",
        sales: "+380%",
        time: "5 weeks"
      },
      beforeImage: "/images/web_design_0.jpg",
      afterImage: "/images/web_design_1.jpg",
      testimonial: "Cart abandonment dropped by 60%. Sales are through the roof!",
      client: "Sarah Johnson, Founder"
    }
  ];

  const metrics = [
    {
      icon: <TrendingUp className="h-6 w-6 text-accent" />,
      label: "Average Traffic Increase",
      value: "200%",
      description: "Across all projects"
    },
    {
      icon: <MousePointer className="h-6 w-6 text-accent" />,
      label: "Conversion Rate Improvement", 
      value: "225%",
      description: "Better user experience"
    },
    {
      icon: <Clock className="h-6 w-6 text-accent" />,
      label: "Average Project Timeline",
      value: "4 weeks",
      description: "From start to launch"
    },
    {
      icon: <Star className="h-6 w-6 text-accent" />,
      label: "Client Satisfaction",
      value: "98%",
      description: "Would recommend us"
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="container mx-auto px-4 text-center">
          <Badge className="bg-primary/10 text-primary border-primary/20 font-body mb-4">
            Case Studies
          </Badge>
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-foreground mb-6">
            Real Results for
            <span className="text-gradient"> Real Businesses</span>
          </h1>
          <p className="font-body text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            See how we've transformed businesses across industries with modern, 
            conversion-focused websites that deliver measurable results.
          </p>
        </div>
      </section>

      {/* Metrics Overview */}
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {metrics.map((metric, index) => (
              <Card key={index} className="hover-lift shadow-card text-center">
                <CardContent className="p-6">
                  <div className="flex justify-center mb-3">
                    {metric.icon}
                  </div>
                  <div className="font-heading text-2xl md:text-3xl font-bold text-primary mb-1">
                    {metric.value}
                  </div>
                  <div className="font-body text-sm font-semibold text-foreground mb-1">
                    {metric.label}
                  </div>
                  <div className="font-body text-xs text-muted-foreground">
                    {metric.description}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="section-padding bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              Success Stories
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              Every project is unique, but the results speak for themselves. 
              Here are some of our favorite transformations.
            </p>
          </div>

          <div className="space-y-16">
            {caseStudies.map((study, index) => (
              <Card key={index} className="overflow-hidden shadow-elegant">
                <CardContent className="p-0">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                    {/* Before/After Images */}
                    <div className="relative">
                      <Tabs defaultValue="before" className="w-full">
                        <TabsList className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur-sm">
                          <TabsTrigger value="before" className="font-body text-sm">Before</TabsTrigger>
                          <TabsTrigger value="after" className="font-body text-sm">After</TabsTrigger>
                        </TabsList>
                        <TabsContent value="before" className="mt-0">
                          <img 
                            src={study.beforeImage}
                            alt={`${study.title} - Before`}
                            className="w-full h-64 lg:h-full object-cover"
                          />
                        </TabsContent>
                        <TabsContent value="after" className="mt-0">
                          <img 
                            src={study.afterImage}
                            alt={`${study.title} - After`}
                            className="w-full h-64 lg:h-full object-cover"
                          />
                        </TabsContent>
                      </Tabs>
                    </div>

                    {/* Content */}
                    <div className="p-8 lg:p-12 space-y-6">
                      <div>
                        <Badge className="bg-accent/10 text-accent border-accent/20 font-body mb-3">
                          {study.category}
                        </Badge>
                        <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-4">
                          {study.title}
                        </h3>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <h4 className="font-heading font-semibold text-foreground mb-2">Challenge</h4>
                          <p className="font-body text-muted-foreground text-sm">
                            {study.challenge}
                          </p>
                        </div>
                        
                        <div>
                          <h4 className="font-heading font-semibold text-foreground mb-2">Solution</h4>
                          <p className="font-body text-muted-foreground text-sm">
                            {study.solution}
                          </p>
                        </div>
                      </div>

                      {/* Results Grid */}
                      <div className="grid grid-cols-2 gap-4 py-4">
                        <div className="text-center">
                          <div className="font-heading text-2xl font-bold text-accent">
                            {study.results.traffic}
                          </div>
                          <div className="font-body text-xs text-muted-foreground">Traffic</div>
                        </div>
                        <div className="text-center">
                          <div className="font-heading text-2xl font-bold text-accent">
                            {study.results.conversions}
                          </div>
                          <div className="font-body text-xs text-muted-foreground">Conversions</div>
                        </div>
                        <div className="text-center">
                          <div className="font-heading text-2xl font-bold text-accent">
                            {Object.values(study.results)[2]}
                          </div>
                          <div className="font-body text-xs text-muted-foreground">
                            {Object.keys(study.results)[2].charAt(0).toUpperCase() + Object.keys(study.results)[2].slice(1)}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="font-heading text-2xl font-bold text-primary">
                            {study.results.time}
                          </div>
                          <div className="font-body text-xs text-muted-foreground">Timeline</div>
                        </div>
                      </div>

                      {/* Testimonial */}
                      <div className="bg-muted/50 p-4 rounded-lg">
                        <p className="font-body text-sm text-foreground italic mb-2">
                          "{study.testimonial}"
                        </p>
                        <p className="font-body text-xs text-muted-foreground font-semibold">
                          — {study.client}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Highlight */}
      <section className="section-padding bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="font-heading text-3xl md:text-4xl font-bold">
              Ready to Be Our Next Success Story?
            </h2>
            <p className="font-body text-lg text-primary-foreground/80">
              Every successful project starts with a conversation. Let's discuss your goals 
              and create a website that delivers real results for your business.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-12">
              <div className="space-y-3">
                <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center mx-auto">
                  <Users className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="font-heading font-semibold">Free Consultation</h3>
                <p className="font-body text-sm text-primary-foreground/80">
                  We'll analyze your current site and discuss your goals
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center mx-auto">
                  <Eye className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="font-heading font-semibold">Custom Strategy</h3>
                <p className="font-body text-sm text-primary-foreground/80">
                  We'll create a tailored plan for your specific needs
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center mx-auto">
                  <TrendingUp className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="font-heading font-semibold">Measurable Results</h3>
                <p className="font-body text-sm text-primary-foreground/80">
                  Track your success with clear metrics and reporting
                </p>
              </div>
            </div>

            <Button 
              asChild 
              size="lg" 
              className="bg-accent hover:bg-accent/90 text-accent-foreground font-heading font-semibold text-lg px-8 py-6"
            >
              <Link to="/booking">
                Schedule Your Free Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;